import React, { useState, useEffect, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { DateTime } from "luxon";
import Select from "react-select";
import countries from "./countries"; // If you have a local countries array, otherwise remove
// import { useNavigate } from "react-router-dom"; // Not used in the sample

// Hard-coded sample privacy terms component
const PublicPagePrivacyTerms = ({ businessName }) => (
  <div
    style={{
      padding: "10px",
      border: "1px solid #ccc",
      borderRadius: "8px",
      backgroundColor: "#f9f9f9",
      maxHeight: "400px",
      overflowY: "auto",
    }}
  >
    <h2>Privacy Terms for Booking on Rili Day</h2>
    <p>
      <strong>Effective Date:</strong> February 2025
    </p>
    <h3>1. Introduction</h3>
    <p>
      By booking a service through Rili Day (www.rili.day) for {businessName},
      you agree to the following privacy terms.
    </p>
    <h3>2. Information We Collect</h3>
    <p>
      We collect your name, email, phone number, and address (if required) to
      process your service request.
    </p>
    <h3>3. How We Use Your Information</h3>
    <ul>
      <li>
        Your information will be submitted to {businessName} for service request
        verification and communication (in real usage).
      </li>
      <li>
        Rili Day will only use your data for sending booking notifications and
        service-related updates.
      </li>
    </ul>
    <h3>4. Your Rights</h3>
    <p>
      You can request the removal of your data from Rili Day at any time by
      contacting us at <a href="mailto:mail@rili.day">mail@rili.day</a>.
    </p>
    <h3>5. Data Protection</h3>
    <p>We comply with GDPR, PIPEDA, and US privacy laws to protect your data.</p>
  </div>
);

const SamplePublicPage = () => {
  // Hard-coded business info
  const [businessData] = useState({
    name: "Sample Business",
    description:
      "Demo description for your sample dashboard page.",
    services: [
      { name: "Consultation", duration: 30 },
      { name: "Maintenance", duration: 60 },
    ],
    business_phone: "555-123-4567",
    business_email: "info@samplebiz.com",
    business_address: "123 Demo Street, Example City",
    timezone: "America/Toronto",
    req_cus_info: {
      name: true,
      email: true,
      phone: false,
      address: false,
    },
  });

  // Hard-coded date range: today + next 7 days
  const [availableDates, setAvailableDates] = useState([]);
  // Raw array for the time slots per date in the calendar
  const [availability, setAvailability] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const bookingSectionRef = useRef(null);
  const calendarRef = useRef(null);
  const serviceRef = useRef(null);
  // Customer info form
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    state: "",
    city: "",
    zipCode: "",
    streetAddress: "",
    additionalInfo: "",
  });

  // UI states
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Required fields from the business data
  const [reqCusInfo] = useState(businessData.req_cus_info);

  // For privacy checkbox
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  // Generate a 7-day window of availability, starting from today
  useEffect(() => {
    const today = DateTime.now().setZone(businessData.timezone || "UTC");
    const dates = [];
    const availObj = {};

    // For each of the next 8 days (today + next 7), create a date
    for (let i = 0; i < 8; i++) {
      const day = today.plus({ days: i });
      const isoDate = day.toISODate();
      dates.push(isoDate);

      // Hard-coded available slots for 10:00, 14:00, 16:00
      availObj[isoDate] = [
        day.set({ hour: 10, minute: 0 }).toUTC().toISO(),
        day.set({ hour: 14, minute: 0 }).toUTC().toISO(),
        day.set({ hour: 16, minute: 0 }).toUTC().toISO(),
      ];
    }

    setAvailableDates(dates);
    setAvailability(availObj);

    // Set initial default service
    if (businessData.services && businessData.services.length > 0) {
      // E.g., pick the first service
      setSelectedService(businessData.services[0]);
    }
  }, [businessData]);

  useEffect(() => {
    if (selectedDate && bookingSectionRef.current) {
      bookingSectionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedDate]);

  // If user picks a date from the calendar, show the slots
  const handleDateClick = (info) => {
    setError("");
    setSuccessMessage("");

    const isoDate = DateTime.fromFormat(info.dateStr, "yyyy-MM-dd", {
      zone: businessData.timezone || "UTC",
    }).toISODate();

    if (availability[isoDate]) {
      // Convert each UTC slot to local zone
      const localSlots = availability[isoDate].map((utcSlot) =>
        DateTime.fromISO(utcSlot, { zone: "UTC" }).setZone(businessData.timezone).toISO()
      );
      setAvailableSlots(localSlots);
      // Set first slot as selectedDate by default
      setSelectedDate(
        DateTime.fromISO(localSlots[0], { zone: businessData.timezone }).toJSDate()
      );
    } else {
      setAvailableSlots([]);
      setSelectedDate(null);
    }

    // Scroll to booking section
    /*setTimeout(() => {
      if (bookingSectionRef.current) {
        bookingSectionRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 0);*/
  };

  // If user selects a different service from the dropdown
  const handleServiceChange = (serviceName) => {
    const service = businessData.services.find((s) => s.name === serviceName);
    if (service) {
      setSelectedService(service);
    }
  };

  // If user picks a slot from the slot dropdown
  const handleSlotSelect = (slotIso) => {
    if (!slotIso) return;
    const localDate = DateTime.fromISO(slotIso, {
      zone: businessData.timezone,
    }).toJSDate();
    setSelectedDate(localDate);
  };

  // Submit the booking request (just a simulation)
  const handleBookingSubmit = () => {
    setError("");
    setSuccessMessage("Demo only! Register to build your own page!");
    
  };

  return (
    <div style={styles.container}>
      <div style={styles.topSection}>
          <p style={{ color: "green", fontWeight: "bold" }}>
            Claim www.rili.day/yourBusinessName and <br />
            build your own booking website like the demo page below.<br /><br />
            Never miss a customer booking while on a job or during sleep.<br /><br />
            Click the Sample Dashboard tab above to see how it works.<br /><br />
          </p>
          <p style={{ color: "grey", fontSize: "12px" }}>
           Below is a demo page, data is not saved or sent anywhere.<br />
          Register to build your own page!
          </p>
          
        
      </div>
      {/* Sample Business Info */}
      <h1 style={styles.title}>{businessData.name}</h1>
      <p style={styles.description}>{businessData.description}</p>
      {businessData.services && businessData.services.length > 0 && (
        <>
          <p style={{marginBottom: '10px'}}><strong>Select a service</strong></p>
          <div style={styles.servicesList}>
            {businessData.services.map((service, index) => (
              <div
                key={index}
                style={{
                  ...styles.serviceCard,
                  ...(selectedService && selectedService.name === service.name ? { border: '2px solid #2c3e50', boxShadow: '0 4px 12px rgba(44,62,80,0.12)' } : {})
                }}
                onClick={() => {
                  setSelectedService(service);
                  calendarRef.current.scrollIntoView({ behavior: "smooth" });
                }}
              >
                <div style={styles.serviceInfo}>
                  <p style={styles.serviceName}><strong>{service.name}</strong></p>
                  <span style={styles.serviceDuration}>{service.duration} minutes</span>
                </div>
                {service.description && (
                  <p style={styles.serviceDescription}>{service.description}</p>
                )}
              </div>
            ))}
          </div>
        </>
      )}
      {businessData.business_phone && (
        <p>
          <strong>Phone:</strong> {businessData.business_phone}
        </p>
      )}
      {businessData.business_email && (
        <p>
          <strong>Email:</strong> {businessData.business_email}
        </p>
      )}
      {businessData.business_address && (
        <p>
          <strong>Address:</strong> {businessData.business_address}
        </p>
      )}

      {/* Calendar Section */}
      <div style={styles.calendarSection} ref={calendarRef}>
        <p style={styles.startPrompt}>
          Click
          <span style={styles.colorBlock}></span>
          on the calendar to get started.
        </p>
        <FullCalendar
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          height="auto"
          headerToolbar={{
            left: "prev,next today",
            center: "",
            right: "title",
          }}
          titleFormat={{ month: "long" }}
          dayHeaderFormat={{ weekday: "narrow" }}
          events={availableDates.map((date) => ({
            title: "",
            start: date,
            display: "background",
          }))}
          dateClick={handleDateClick}
        />
      </div>

      {/* Booking Section */}
      {selectedDate && (
        <div style={styles.bookingSection} ref={bookingSectionRef}>
          <h2>
            Available Slots on{" "}
            {selectedDate.toLocaleDateString([], {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </h2>

          {/* Service Dropdown */}
          <label style={styles.label}>Select Service</label>
          <select
            style={styles.input}
            value={selectedService ? selectedService.name : ""}
            onChange={(e) => handleServiceChange(e.target.value)}
          >
            {businessData.services.map((service, index) => (
              <option key={index} value={service.name}>
                {service.name} - {service.duration} minutes
              </option>
            ))}
          </select>

          {/* Slots Dropdown */}
          <label style={styles.label}>Available Slots</label>
          {availableSlots.length > 0 ? (
            <select
              style={styles.input}
              value={selectedDate ? DateTime.fromJSDate(selectedDate).toISO() : ""}
              onChange={(e) => handleSlotSelect(e.target.value)}
            >
              {availableSlots.map((slot, index) => (
                <option key={index} value={slot}>
                  {DateTime.fromISO(slot, {
                    zone: businessData.timezone || "UTC",
                  }).toFormat("hh:mm a")}
                </option>
              ))}
            </select>
          ) : (
            <p>No available slots for this date.</p>
          )}

          {/* Forms */}
          {reqCusInfo.name && (
            <>
              <label style={styles.label}>Name</label>
              <input
                type="text"
                style={styles.input}
                value={customerInfo.name}
                onChange={(e) =>
                  setCustomerInfo({ ...customerInfo, name: e.target.value })
                }
                required
              />
            </>
          )}

          {/* Email is always required */}
          <label style={styles.label}>Email</label>
          <input
            type="email"
            style={styles.input}
            value={customerInfo.email}
            onChange={(e) =>
              setCustomerInfo({ ...customerInfo, email: e.target.value })
            }
          />

          {reqCusInfo.phone && (
            <>
              <label style={styles.label}>Phone</label>
              <input
                type="tel"
                style={styles.input}
                value={customerInfo.phone}
                onChange={(e) =>
                  setCustomerInfo({
                    ...customerInfo,
                    phone: e.target.value.replace(/\D/g, ""),
                  })
                }
                required
              />
            </>
          )}

          {reqCusInfo.address && (
            <div>
              <label style={styles.label}>Address</label>
              <label style={styles.label}>Country</label>
              <Select
                options={countries}
                value={countries.find((c) => c.value === customerInfo.country)}
                onChange={(selected) => {
                  setCustomerInfo({
                    ...customerInfo,
                    country: selected.value,
                    state: "",
                  });
                }}
                styles={customSelectStyles}
              />
              {customerInfo.country && (
                <>
                  <label style={styles.label}>State/Province</label>
                  <Select
                    options={
                      countries.find((c) => c.value === customerInfo.country)
                        ?.states || []
                    }
                    value={countries
                      .find((c) => c.value === customerInfo.country)
                      ?.states.find((s) => s.value === customerInfo.state)}
                    onChange={(selected) =>
                      setCustomerInfo({ ...customerInfo, state: selected.value })
                    }
                    styles={customSelectStyles}
                  />
                </>
              )}

              <label style={styles.label}>City</label>
              <input
                type="text"
                style={styles.input}
                value={customerInfo.city}
                onChange={(e) =>
                  setCustomerInfo({ ...customerInfo, city: e.target.value })
                }
                required
              />

              <label style={styles.label}>Street Address</label>
              <input
                type="text"
                style={styles.input}
                value={customerInfo.streetAddress}
                onChange={(e) =>
                  setCustomerInfo({
                    ...customerInfo,
                    streetAddress: e.target.value,
                  })
                }
                required
              />

              <label style={styles.label}>Zip/Postal Code</label>
              <input
                type="text"
                style={styles.input}
                value={customerInfo.zipCode}
                onChange={(e) =>
                  setCustomerInfo({ ...customerInfo, zipCode: e.target.value })
                }
                required
              />
            </div>
          )}

          <label style={styles.label}>Additional Information</label>
          <textarea
            style={styles.input}
            value={customerInfo.additionalInfo}
            onChange={(e) =>
              setCustomerInfo({ ...customerInfo, additionalInfo: e.target.value })
            }
          />
          <br />

          {/* Privacy Terms Checkbox */}
          <div style={{ margin: "10px 0" }}>
            <input
              type="checkbox"
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
              required
            />
            <span>
              {" "}
              Check to agree with the{" "}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setShowPrivacy(!showPrivacy);
                }}
              >
                privacy terms
              </a>
              .
            </span>
          </div>
          {showPrivacy && (
            <PublicPagePrivacyTerms businessName={businessData.name} />
          )}

          {/* Submit button */}
          <button
            style={{
              ...styles.button,
              opacity: isSubmitting ? 0.5 : 1,
              cursor: isSubmitting ? "not-allowed" : "pointer",
            }}
            onClick={handleBookingSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit Request"}
          </button>
        </div>
      )}

      {/* Error / Success Messages */}
      {error && <p style={styles.error}>{error}</p>}
      {successMessage && <p style={styles.success}>{successMessage}</p>}
    </div>
  );
};

// Styles
const styles = {
  container: {
    padding: "20px",
    paddingTop: "0px",
    maxWidth: "800px",
    margin: "0 auto",
    fontFamily: "Arial, sans-serif",
    lineHeight: "1.6"
  },
  title: { fontSize: "32px", marginBottom: "20px" },
  description: {
    fontSize: "16px",
    marginBottom: "20px",
    whiteSpace: "pre-line", // Preserves new lines
    textAlign: "justify", // Justifies text
  },
  calendarSection: { marginBottom: "30px" },
  bookingSection: { marginTop: "20px" },
  label: { display: "block", marginBottom: "5px", fontWeight: "bold" },
  input: {
    width: "80%",
    padding: "10px",
    marginBottom: "10px",
    borderRadius: "5px",
    border: "1px solid #ccc",
  },
  button: {
    padding: "10px 20px",
    backgroundColor: "#2c3e50",
    color: "#fff",
    border: "none",
    borderRadius: "5px",
  },
  error: { color: "red", marginTop: "10px" },
  success: { color: "green", marginTop: "10px" },
  colorBlock: {
    display: "inline-block",
    width: "40px",
    height: "15px",
    backgroundColor: "rgba(17, 129, 0, 0.338)",
    borderRadius: "3px",
    border: "",
    margin: "0 5px",
  },
  startPrompt: {
    display: "block",
    marginTop: "60px",
    marginBottom: "30px",
    fontWeight: "bold",
  },
  servicesList: {
    display: 'grid',
    gap: '20px',
    marginBottom: '30px',
  },
  serviceCard: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '10px',
    boxShadow: '0 0 4px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, border 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
      backgroundColor: '#f8f9fa',
    }
  },
  serviceInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  serviceName: {
    margin: 0,
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  serviceDuration: {
    fontSize: '14px',
    color: '#666',
    backgroundColor: '#f0f0f0',
    padding: '4px 12px',
    borderRadius: '12px',
    marginLeft: 'auto',
  },
  serviceDescription: {
    marginTop: '10px',
    marginBottom: '0',
    fontSize: '15px',
    color: '#555',
    lineHeight: '1.6',
  },
};

const customSelectStyles = {
  control: (base) => ({
    ...base,
    padding: "5px",
    borderRadius: "5px",
    border: "1px solid #ccc",
    fontSize: "16px",
  }),
};

export default SamplePublicPage;
